import React, { useState, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';
import useResizeObserver from '@/hooks/useResizeObserver';

interface LegendItem {
  label: string;
  percentage: string;
  color: string;
  value: number;
  growth: number | null;
}

interface DonutChartProps {
  legendItems: LegendItem[];
  totalHousingUnits: number;
  className?: string;
}

const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
  const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
  return {
    x: centerX + (radius * Math.cos(angleInRadians)),
    y: centerY + (radius * Math.sin(angleInRadians))
  };
};

const DonutArc: React.FC<{
  item: LegendItem;
  centerX: number;
  centerY: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  onClick: () => void;
}> = ({ item, centerX, centerY, innerRadius, outerRadius, startAngle, endAngle, onClick }) => {
  // To avoid issues with 360 degree arcs in SVG
  if (endAngle - startAngle >= 360) {
    endAngle = startAngle + 359.99;
  }
  
  const startOuter = polarToCartesian(centerX, centerY, outerRadius, startAngle);
  const endOuter = polarToCartesian(centerX, centerY, outerRadius, endAngle);
  const startInner = polarToCartesian(centerX, centerY, innerRadius, startAngle);
  const endInner = polarToCartesian(centerX, centerY, innerRadius, endAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  const d = [
    "M", startOuter.x, startOuter.y,
    "A", outerRadius, outerRadius, 0, largeArcFlag, 1, endOuter.x, endOuter.y,
    "L", endInner.x, endInner.y,
    "A", innerRadius, innerRadius, 0, largeArcFlag, 0, startInner.x, startInner.y,
    "Z"
  ].join(" ");

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };

  return (
    <path
      d={d}
      fill={item.color}
      className="transition-all duration-300 ease-in-out cursor-pointer"
      onClick={handleClick}
    />
  );
};

export const snapshot_DonutChart: React.FC<DonutChartProps> = ({ 
  legendItems, 
  totalHousingUnits, 
  className = "" 
}) => {
  const [selectedItem, setSelectedItem] = useState<LegendItem | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { width, height } = useResizeObserver(chartContainerRef);

  const donut_data = useMemo(() => legendItems, [legendItems]);

  const handleItemClick = (item: LegendItem) => {
    setSelectedItem(item === selectedItem ? null : item);
  };

  const handleChartClick = () => {
    setSelectedItem(null);
  };

  const formatNumber = (num: number) => new Intl.NumberFormat().format(Math.round(num));

  const GrowthIndicator = ({ growth }: { growth: number | null }) => {
    if (growth === null) {
      return (
        <div className="justify-center items-center self-stretch flex my-auto px-3 py-1.5 rounded-[20px] bg-gray-100 text-gray-500">
          <span className="text-xs leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>N/A</span>
        </div>
      );
    }
    const isPositive = growth >= 0;
    const tickerStyles = isPositive 
      ? "text-[#4B7400] bg-[#F3FAE5]" 
      : "text-[#B30000] bg-[#FFDADA]";

    return (
      <div className={`justify-center items-center self-stretch flex my-auto px-3 py-1.5 rounded-[20px] ${tickerStyles}`}>
        <span className={`flex items-center text-xs gap-1 leading-tight align-middle chart-text-shift`} style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
          <span 
            className={`material-symbols-outlined text-sm transition-transform duration-200 ${!isPositive ? 'rotate-180' : ''} leading-tight align-middle chart-text-shift`} style={{ fontSize: '16px', lineHeight: '1.2', verticalAlign: 'middle' }}
          >
            arrow_upward_alt
          </span>
          {Math.abs(growth).toFixed(1)}% YoY
        </span>
      </div>
    );
  };

  const dynamicRadius = Math.min(width, height) / 2;
  const radius = dynamicRadius > 0 ? dynamicRadius - 10 : 90; // 10 for padding
  const strokeWidth = radius / 4;
  const outerRadius = radius;
  const innerRadius = radius - strokeWidth;
  const centerX = width / 2;
  const centerY = height / 2;

  const totalValue = useMemo(() => donut_data.reduce((sum, item) => sum + item.value, 0), [donut_data]);

  const segments = useMemo(() => {
    const segments: { item: LegendItem; startAngle: number; endAngle: number }[] = [];
    let currentAngle = 0;
    donut_data.forEach(item => {
      const sliceAngle = totalValue > 0 ? (item.value / totalValue) * 360 : 0;
      const startAngle = currentAngle;
      currentAngle += sliceAngle;
      const endAngle = currentAngle;
      segments.push({ item, startAngle, endAngle });
    });
    return segments;
  }, [donut_data, totalValue]);

  return (
    <div className={cn("bg-white flex w-full max-w-full flex-col overflow-hidden items-stretch text-base font-medium text-center leading-[1.3] justify-center py-px", className)}>
      <div className="flex items-center max-donut:flex-col donut:flex-row donut:justify-center w-full">
        <div
          ref={chartContainerRef}
          className="relative flex justify-center items-center donut:w-1/2 w-full min-h-[300px] p-4"
          onClick={handleChartClick}
        >
          <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
          >
            {segments.map(({ item, startAngle, endAngle }, index) => (
              <DonutArc
                key={`arc-${index}`}
                item={item}
                centerX={centerX}
                centerY={centerY}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                onClick={() => handleItemClick(item)}
              />
            ))}
            {donut_data.length > 1 && segments.map(({ item, startAngle }, index) => {
              if (item.value === 0) return null;

              const p1 = polarToCartesian(centerX, centerY, innerRadius, startAngle);
              const p2 = polarToCartesian(centerX, centerY, outerRadius, startAngle);
              return (
                <line
                  key={`divider-${index}`}
                  x1={p1.x}
                  y1={p1.y}
                  x2={p2.x}
                  y2={p2.y}
                  stroke="white"
                  strokeWidth={2}
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-2 pointer-events-none">
            {selectedItem ? (
              <>
                <div className="text-sm font-bold leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle', color: selectedItem.color}}>{selectedItem.label}</div>
                <div className="text-3xl font-bold mt-1 leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>{formatNumber(selectedItem.value)}</div>
                <div className="text-sm text-gray-600 leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>units</div>
                <div className="mt-2">
                  <GrowthIndicator growth={selectedItem.growth} />
                </div>
              </>
            ) : (
              <>
                <div className="text-3xl font-bold leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>{formatNumber(totalHousingUnits)}</div>
                <div className="text-sm text-gray-600 leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>Total Units</div>
              </>
            )}
          </div>
        </div>
        <div className="flex min-w-60 min-h-[300px] flex-col items-stretch justify-center donut:w-1/2 w-full p-4">
          {donut_data.map((item, index) => (
            <div
              key={index}
              className={cn(
                "flex w-full items-center gap-5 cursor-pointer p-2 rounded-md transition-colors donut-legend-item",
                { "bg-gray-100": selectedItem === item }
              )}
              style={{ marginTop: index === 0 ? 0 : '16px' }}
              onClick={() => handleItemClick(item)}
            >
              <div className="self-stretch flex items-center gap-2.5 text-[#212121] flex-1 shrink basis-[0%] my-auto">
                <div 
                  className="self-stretch flex w-4 shrink-0 h-4 my-auto rounded-[50%]"
                  style={{ backgroundColor: item.color }}
                />
                <div className="text-[#212121] self-stretch my-auto whitespace-nowrap donut-legend-text">
                  {item.label}
                </div>
              </div>
              <div className="text-[#212121] self-stretch my-auto whitespace-nowrap donut-legend-text">
                {item.percentage}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};